import {AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {DateUtilsService} from "../../../../shared/services/dateUtils.service";
import {PaginatorService} from "../../../../shared/services/paginator.service";
import {UtilsService} from "../../../../shared/services/utils.service";
import {RetailerReportLine, RetailerTradingReport} from "../trading-report.interface";
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {FormControl, FormGroup} from '@angular/forms';

@Component({
  selector: 'app-retailer-trading-report',
  templateUrl: './retailer-trading-report.component.html',
  styleUrls: ['./retailer-trading-report.component.scss']
})
export class RetailerTradingReportComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('paginator') paginator!: MatPaginator;
  @ViewChild('sort') sort!: MatSort;
  @Input() retailerReport!: RetailerTradingReport;
  @Input() reportName!: string;

  reportColumns: string[] = ['retailerId', 'retailerCompanyName', 'transactionDate', 'paymentMethod', 'amountReceived'];
  retailerReportDatasource = new MatTableDataSource<RetailerReportLine>();
  tableTitle = 'Retailer Trading Report';
  exportModel = [
    {key: 'retailerId', displayName: 'Retailer Id'},
    {key: 'retailerCompanyName', displayName: 'Company Name'},
    {key: 'transactionDate', displayName: 'Transaction Date'},
    {key: 'paymentMethod', displayName: 'Payment Method'},
    {key: 'amountReceived', displayName: 'Amount Received'}
  ];

  reportFormGroup: FormGroup;

  constructor(public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private paginatorService: PaginatorService,
              public loader: LoadingService,
              ) {
    this.setFilterPredicate();
    this.reportFormGroup = new FormGroup({
      retailerCompanyName: new FormControl(null),
      paymentMethod: new FormControl(null),
    });

    this.reportFormGroup.valueChanges.subscribe(formValues => {
      this.applyFilter(formValues);
    });
  }

  ngOnInit() {
    this.setReportData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['retailerReport']) {
      this.setReportData();
    }
  }

  ngAfterViewInit() {
    this.paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
    this.retailerReportDatasource.paginator = this.paginator;
    this.retailerReportDatasource.sort = this.sort;
  }

  setReportData() {
    this.retailerReportDatasource.data = [ ...this.retailerReport.retailerReport ];
  }

  setFilterPredicate() {
    this.retailerReportDatasource.filterPredicate = (data: RetailerReportLine, filter: string) => {
      const filterValues = JSON.parse(filter);
      const matchFilter = [];

      const retailerCompanyNameMatch = data.retailerCompanyName.toLowerCase().includes(filterValues.retailerName.toLowerCase());
      matchFilter.push(retailerCompanyNameMatch);

      const paymentMethodMatch = data.paymentMethod.toLowerCase().includes(filterValues.paymentMethod.toLowerCase());
      matchFilter.push(paymentMethodMatch);

      return matchFilter.every(Boolean);
    };
  }

  applyFilter(formValues: any) {
    const filterValues = {
      retailerName: formValues.retailerCompanyName || '',
      paymentMethod: formValues.paymentMethod || ''
    };
    this.retailerReportDatasource.filter = JSON.stringify(filterValues);

    if (this.retailerReportDatasource.paginator) {
      this.retailerReportDatasource.paginator.firstPage();
    }
  }

  getAmountReceivedTotal(): number {
    return this.retailerReportDatasource.filteredData.map(item => item.amountReceived)
      .reduce((acc, val) => acc + val, 0);
  }

  exportToCsv() {
    const dataWithDisplayLabels = this.retailerReportDatasource.filteredData.map(
      (item: any) => {
        const newItem: any = {};
        this.exportModel.forEach((column) => {
          newItem[column.displayName.toUpperCase()] = item[column.key];
        });
        return newItem;
      }
    );

    const csv = Papa.unparse(dataWithDisplayLabels);
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);

    this.downloadFile(url, `${this.tableTitle}-${new Date().toISOString()}.csv`);

  }

  exportToExcel() {

    const dataWithDisplayLabels = this.retailerReportDatasource.filteredData.map(
      (item: any) => {
        const newItem: any = {};
        this.exportModel.forEach((column) => {
          newItem[column.displayName.toUpperCase()] = item[column.key];
        });
        return newItem;
      }
    );

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataWithDisplayLabels);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${this.tableTitle}-${new Date().toISOString()}.xlsx`);
  }

  exportToPdf() {

    const doc = new jsPDF('landscape', 'pt', 'a4');
    const columns = this.exportModel.map((col) => col.displayName);
    const body = this.retailerReportDatasource.filteredData.map((row: any) =>
      this.exportModel.map((col) => row[col.key]?.toString() || '')
    );
    autoTable(doc, {
      head: [columns],
      body: body,
    });
    doc.save(`${this.tableTitle}-${new Date().toISOString()}.pdf`);

  }

  private downloadFile(url: string, fileName: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
